<template>
  <div ref="root" class="route-endpoints" @click.stop @mousedown.stop>
    <template v-for="end in ends" :key="end.which">
      <!-- A div, not a section: the app gives every <section> a 4rem bottom margin. -->
      <div class="route-endpoints__section" :class="`route-endpoints__section--${end.which}`">
        <!-- The end as a field, its marker saying which: click it to search for somewhere else. -->
        <div class="route-endpoints__field-row">
        <button
          type="button"
          class="route-endpoints__row"
          :class="{ 'route-endpoints__row--editing': editing === end.which, 'route-endpoints__row--unset': !end.label }"
          :aria-label="end.which === 'start' ? $t('plan_trail.route_endpoints.start') : $t('plan_trail.route_endpoints.end')"
          @click="toggle(end.which)"
        >
          <!-- The same marker the map draws at this end (see endpointMarker). -->
          <span class="route-endpoints__marker" aria-hidden="true">
            <img v-if="end.marker.kind === 'start'" :src="startIcon" alt="" />
            <img v-else-if="end.marker.kind === 'finish'" :src="finishIcon" alt="" />
            <span v-else-if="end.marker.kind === 'stage'" class="route-endpoints__stage">{{ end.marker.number }}</span>
            <span v-else class="route-endpoints__join"></span>
          </span>
          <span v-if="end.label" class="route-endpoints__value">{{ end.label }}</span>
          <span v-else class="route-endpoints__value route-endpoints__value--unset">{{ $t('plan_trail.route_endpoints.not_set') }}</span>
        </button>
        <!-- Carry the route on from its end: the end stays, as a control point. -->
        <button
          v-if="end.which === 'end' && endCoord"
          type="button"
          class="route-endpoints__extend"
          :class="{ 'route-endpoints__extend--editing': editing === 'extend' }"
          :title="$t('plan_trail.route_endpoints.continue_hint')"
          :aria-label="$t('plan_trail.route_endpoints.continue')"
          @click="toggle('extend')"
        >
          <span class="material-icons" aria-hidden="true">add</span>
        </button>
        </div>

        <!-- The search sits right under the end being changed. -->
        <PlaceSearchPanel
          v-if="editing === end.which"
          :key="`search-${end.which}`"
          class="route-endpoints__search"
          :focus="focus"
          :load-places="loadNearby"
          @pick="pickPlace"
          @close="close"
        />
        <PlaceSearchPanel
          v-if="end.which === 'end' && editing === 'extend'"
          key="search-extend"
          class="route-endpoints__search"
          :focus="focus"
          :placeholder="$t('plan_trail.route_endpoints.search_next')"
          :load-places="loadNearby"
          @pick="pickPlace"
          @close="close"
        />
      </div>

      <!-- The day's highlights read as the stops between its start and end. -->
      <DayHighlights v-if="end.which === 'start' && dayNumber != null" inline :day-number="dayNumber" class="route-endpoints__highlights" />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import PlaceSearchPanel from './PlaceSearchPanel.vue';
import DayHighlights from './DayHighlights.vue';
import { attachedDestinationAtCoord } from '../services/stage-destinations';
import { endpointLabel, endpointMarker } from '../services/route-endpoints';
import startIcon from '../../../images/planner/route-start.png';
import finishIcon from '../../../images/planner/route-finish.png';
import { addressKey, nearbyPlaces, rememberedAddresses as addressCache } from '../services/nearby-places';
import { extendRouteToPick, moveEndpointToPick } from '../services/endpoint-picks';
import type { PlacePick } from '../services/place-picks';
import { stadiaGeocodingService } from '../../../utils/services/StadiaGeocodingService';

type Which = 'start' | 'end';
/** What's being changed: an end, or `extend` — a new end beyond the current one. */
type Editing = Which | 'extend';

/** `dayNumber`: the day whose highlights to list between the two ends — when this route is the whole day. */
const props = defineProps<{ routeKey: string; dayNumber?: number | null }>();
const store = useStore();

const root = ref<HTMLElement | null>(null);
const editing = ref<Editing | null>(null);
const visible = ref(false);

const planTrail = computed(() => store.state.planTrail);
const route = computed(() => planTrail.value?.routes?.[props.routeKey]);
const coordOf = (marker: any): [number, number] | null => {
  const coords = marker?.markerGeometry?.coordinates;
  return coords ? [coords[0], coords[1]] : null;
};
const startCoord = computed(() => coordOf(route.value?.markers?.[0]));
const endCoord = computed(() => {
  const markers = route.value?.markers ?? [];
  return markers.length > 1 ? coordOf(markers[markers.length - 1]) : null;
});

const addressFor = reactive<{ [key: string]: string | null }>({});
const pointKey = addressKey;

async function lookUpAddress(coord: [number, number] | null) {
  if (!coord || !visible.value) return;
  const key = pointKey(coord);
  if (key in addressFor) return;
  if (addressCache.has(key)) {
    addressFor[key] = addressCache.get(key) ?? null;
    return;
  }
  addressFor[key] = null;
  const address = await stadiaGeocodingService.reverse(coord[0], coord[1]);
  addressCache.set(key, address);
  addressFor[key] = address;
}

function labelFor(coord: [number, number] | null): string | null {
  if (!coord) return null;
  const destination = attachedDestinationAtCoord(
    planTrail.value.routes, planTrail.value.stageDestinations, coord, undefined, planTrail.value.trailStartDestination
  );
  return endpointLabel({ destinationName: destination?.name, address: addressFor[pointKey(coord)], coordinates: coord });
}

const markerFor = (which: Which) => endpointMarker(
  planTrail.value.routes, props.routeKey, which,
  store.getters?.['planTrail/stageConnectors'] ?? [], store.getters?.['planTrail/routeJoins'] ?? []
);

const ends = computed(() => [
  { which: 'start' as Which, label: labelFor(startCoord.value), marker: markerFor('start') },
  { which: 'end' as Which, label: labelFor(endCoord.value), marker: markerFor('end') },
]);

watch([startCoord, endCoord, visible], () => {
  lookUpAddress(startCoord.value);
  lookUpAddress(endCoord.value);
}, { immediate: true });

// Only look addresses up for route cards someone can actually see.
let observer: IntersectionObserver | null = null;
onMounted(() => {
  if (typeof IntersectionObserver === 'undefined' || !root.value) {
    visible.value = true;
    return;
  }
  observer = new IntersectionObserver(entries => {
    if (entries.some(entry => entry.isIntersecting)) visible.value = true;
  });
  observer.observe(root.value);
});
onBeforeUnmount(() => observer?.disconnect());

/** Where the end being changed is now — results are ranked by distance from it. */
const focus = computed<[number, number] | null>(() =>
  (editing.value === 'start' ? startCoord.value : endCoord.value ?? startCoord.value) ?? null);

const routeLine = computed(() => (route.value?.lineStrings ?? [])
  .flatMap((lineString: any) => lineString?.lineStringGeometry?.coordinates ?? []));

function loadNearby() {
  const line = routeLine.value.length ? routeLine.value : [startCoord.value].filter(Boolean) as number[][];
  return line.length ? nearbyPlaces(line) : Promise.resolve([]);
}

function toggle(which: Editing) {
  editing.value = editing.value === which ? null : which;
}

function close() {
  editing.value = null;
}

/** Moves the end being changed to what was picked, or carries the route on to it — see services/endpoint-picks.ts. */
async function pickPlace(pick: PlacePick) {
  const which = editing.value;
  close();
  if (!which) return;
  const dayNumber = route.value?.day ?? 1;
  if (which === 'extend') {
    await extendRouteToPick(store.dispatch, { routeKey: props.routeKey, dayNumber, pick });
    return;
  }
  await moveEndpointToPick(store.dispatch, { routeKey: props.routeKey, which, dayNumber, pick });
}

defineExpose({ toggle, pickPlace });
</script>

<style scoped>
.route-endpoints {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin-top: 0.45rem;
}

/* Each end is a field; the marker inside it says which end it is. */
.route-endpoints__section {
  min-width: 0;
}

.route-endpoints__field-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
}

.route-endpoints__row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  padding: 0.5rem 0.6rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  background: var(--dark, #17181D);
  color: #fff;
  font-size: 0.84rem;
  line-height: 1.3;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, background-color 0.15s;
}

.route-endpoints__row:hover {
  border-color: rgba(255, 255, 255, 0.22);
}

.route-endpoints__row--editing {
  border-color: rgba(253, 223, 0, 0.7);
  box-shadow: 0 0 0 3px rgba(253, 223, 0, 0.18);
}

.route-endpoints__marker {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
}

.route-endpoints__marker img {
  width: 18px;
  height: 18px;
}

/* As on the map: the stage badge (utils/stageBadgeIcon.ts)… */
.route-endpoints__stage {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fddf00;
  border: 1.5px solid #17181d;
  color: #17181d;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
}

/* …and the join handle (store/modules/actions/route-joins.ts). */
.route-endpoints__join {
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #fddf00;
}

.route-endpoints__value {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.route-endpoints__value--unset {
  color: #8a8d96;
}

.route-endpoints__search {
  margin-top: 0.35rem;
}

/* A quiet "+" beside the End field: the route can carry on from here. */
.route-endpoints__extend {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 50%;
  background: none;
  color: #a1a4ad;
  cursor: pointer;
  transition: color 0.15s, background 0.15s, border-color 0.15s;
}

.route-endpoints__extend .material-icons {
  font-size: 15px;
}

.route-endpoints__extend:hover,
.route-endpoints__extend--editing {
  color: #17181d;
  background: #fddf00;
  border-color: #fddf00;
}
</style>
